/**
 * Utility functions for extracting and formatting error messages from API responses
 */

export interface ApiErrorResponse {
  success?: boolean
  message?: string
  error?: string
  error_code?: string
  status_code?: number
  details?: Record<string, any>
}

/**
 * Extract detailed error message from Axios error response
 * Handles various error formats and provides user-friendly messages
 * 
 * @param error - The error object (usually from Axios)
 * @param defaultMessage - Fallback message if no error details found
 * @returns Formatted error message string
 */
export function extractErrorMessage(error: any, defaultMessage: string = 'An error occurred'): string {
  console.error('=== Error Handler Debug ===')
  console.error('Full error object:', error)
  console.error('Error response data:', error.response?.data)
  console.error('Error response status:', error.response?.status)
  
  // Safety check - if error is null or undefined, return default
  if (!error) {
    return defaultMessage
  }
  
  // Check if error has response data (Axios error)
  if (error.response?.data) {
    let errorData: any = error.response.data
    const statusCode = error.response.status
    
    // Handle nested error structure: { error: { message, error_code, details }, status_code }
    if (errorData.error && typeof errorData.error === 'object') {
      console.error('Found nested error object:', errorData.error)
      errorData = errorData.error
    }
    
    // Extract the base error message
    let errorMessage = errorData.message || errorData.error || defaultMessage
    console.error('Extracted base message:', errorMessage)
    
    // Extract error_code
    const errorCode = errorData.error_code
    console.error('Error code:', errorCode)
    
    // Extract details
    const details = errorData.details
    console.error('Details object:', details)
    
    // Add specific handling for common HTTP status codes
    switch (statusCode) {
      case 409:
        // Conflict - usually duplicate entries
        if (details && typeof details === 'object') {
          const detailsText = Object.entries(details)
            .map(([key, value]) => `${key}: "${value}"`)
            .join('\n')
          if (detailsText) {
            errorMessage = `${errorMessage}\n${detailsText}`
          }
        }
        if (errorCode) {
          errorMessage = `${errorMessage}\nError code: ${errorCode}`
        }
        console.error('Final 409 error message:', errorMessage)
        break
        
      case 400:
        // Bad Request - validation errors
        if (details && typeof details === 'object') {
          const detailsText = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n')
          if (detailsText) {
            errorMessage = `${errorMessage}\n${detailsText}`
          }
        }
        console.error('Final 400 error message:', errorMessage)
        break
        
      case 404:
        // Not Found
        errorMessage = errorData.message || 'Resource not found'
        break
        
      case 500:
      case 502:
      case 503:
        // Server errors
        errorMessage = errorData.message || 'Server error occurred. Please try again later.'
        if (errorCode) {
          errorMessage = `${errorMessage}\nError code: ${errorCode}`
        }
        console.error('Final 5xx error message:', errorMessage)
        break
        
      default:
        // Other errors - add status code for debugging
        if (statusCode && statusCode !== 200) {
          errorMessage = `${errorMessage}\nStatus: ${statusCode}`
        }
        console.error('Final default error message:', errorMessage)
    }
    
    console.error('=== Returning final error message ===:', errorMessage)
    return errorMessage
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error
  }
  
  // Fallback
  return defaultMessage
}

/**
 * Format error for display with line breaks preserved
 * @param error - The error object
 * @param defaultMessage - Fallback message
 * @returns Object with message and isMultiLine flag
 */
export function formatErrorForDisplay(error: any, defaultMessage: string = 'An error occurred') {
  const message = extractErrorMessage(error, defaultMessage)
  const isMultiLine = message.includes('\n')
  
  return {
    message,
    isMultiLine
  }
}

/**
 * Check if error is a specific HTTP status code
 * @param error - The error object
 * @param statusCode - HTTP status code to check
 * @returns True if error matches the status code
 */
export function isErrorStatus(error: any, statusCode: number): boolean {
  return error?.response?.status === statusCode
}

/**
 * Check if error is a conflict (409) error
 * @param error - The error object
 * @returns True if error is 409 Conflict
 */
export function isConflictError(error: any): boolean {
  return isErrorStatus(error, 409)
}

/**
 * Check if error is a validation (400) error
 * @param error - The error object
 * @returns True if error is 400 Bad Request
 */
export function isValidationError(error: any): boolean {
  return isErrorStatus(error, 400)
}

/**
 * Check if error is a not found (404) error
 * @param error - The error object
 * @returns True if error is 404 Not Found
 */
export function isNotFoundError(error: any): boolean {
  return isErrorStatus(error, 404)
}

/**
 * Check if error is a server (5xx) error
 * @param error - The error object
 * @returns True if error is 5xx Server Error
 */
export function isServerError(error: any): boolean {
  const status = error?.response?.status
  return status >= 500 && status < 600
}

/**
 * Safely convert any value to a string for error display
 * @param value - The value to convert
 * @returns String representation of the value
 */
export function safeErrorString(value: any): string {
  if (value === null || value === undefined) {
    return ''
  }
  
  if (typeof value === 'string') {
    return value
  }
  
  if (value instanceof Error) {
    return value.message
  }
  
  // Try to stringify objects
  try {
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }
  } catch {
    // If stringify fails, use toString
  }
  
  return String(value)
}
